/**
 * Pikestee pack mode ("build your bundle") and quick pick. Styles: assets/bundle-builder.css.
 * - Pack page: any collection opened with ?view=bundle (templates/collection.bundle.json,
 *   sections/main-bundle-builder.liquid). Opening it switches pack mode on for the whole site
 *   (cart attribute _pk_bundle = 1, see sections/site-globals.liquid) until the customer exits it
 *   or places the order.
 * - <bundle-picker> (snippets/bundle-picker.liquid): colour + size under each card, then "Add to pack" (pack mode)
 *   or "Add to cart" (normal mode, product page carousels: the cart drawer opens after the add), then a - n +
 *   stepper for that variant.
 * - <bundle-progress> (snippets/bundle-progress.liquid): progress to the next camp (Base Camp, High Camp,
 *   Summit), in the pack bar and in the cart drawer. Offers: snippets/bundle-tiers.liquid.
 * - <bundle-bar> (snippets/bundle-bar.liquid): sticky bar at the bottom of every page in pack mode
 *   (items, progress, total, Empty pack, Checkout, Exit pack); collection links open in the pack view.
 * Items are counted like the automatic discounts MULTIBUY-4 / MULTIBUY-5: every cart line except the
 * shipping protection. After each change made here the header cart bubble and the cart drawer are
 * re-rendered, so the drawer is up to date but stays closed while the customer keeps shopping in pack mode.
 */
(() => {
  if (window.__pkBundle) return;
  window.__pkBundle = true;

  const cartUrl = window.routes?.cart_url || '/cart';
  const COLLECTION_PATH = /^(\/[a-z]{2}(-[a-z]{2})?)?\/collections\/[^/]+\/?$/i;
  const isProtection = (item) => item.handle === 'shipping-protection' || item.vendor === 'ShopWill';
  const sumQuantity = (items) => items.reduce((sum, item) => sum + item.quantity, 0);
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const formatMoney = (cents, currency) => {
    const whole = cents % 100 === 0;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2,
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  };

  const parseTiers = (json) => {
    try {
      return JSON.parse(json || '[]')
        .filter((tier) => tier.qty > 0)
        .sort((a, b) => a.qty - b.qty);
    } catch (e) {
      return [];
    }
  };

  // Camp reached with `count` items, and the next one
  const tierState = (tiers, count) => ({
    reached: tiers.filter((tier) => tier.qty <= count).pop(),
    next: tiers.find((tier) => tier.qty > count),
  });

  // "2 more items to Base Camp: free shipping", "Base Camp reached! 1 more item to High Camp: 5% off"...
  const tierMessage = (count, { reached, next }) => {
    const lower = (text) => text.charAt(0).toLowerCase() + text.slice(1);
    const more = (missing) => `${missing} more item${missing === 1 ? '' : 's'}`;
    if (!reached && !next) return '';
    if (!reached) {
      if (count === 0) return `Add ${next.qty} items to reach ${next.title}: ${lower(next.reward)}`;
      return `${more(next.qty - count)} to ${next.title}: ${lower(next.reward)}`;
    }
    if (next) return `${reached.title} reached! ${more(next.qty - count)} to ${next.title}: ${lower(next.reward)}`;
    return `\u{1F3D4}\u{FE0F} ${reached.title} reached: ${lower(reached.reward)}!`;
  };

  // Same re-render as Dawn's cart drawer after an add (assets/cart-drawer.js renderContents), without opening it.
  const renderCartSections = (sections) => {
    cart.renderedAt = Date.now();
    const bubble = document.getElementById('cart-icon-bubble');
    const bubbleSection = sections['cart-icon-bubble'] && parseHTML(sections['cart-icon-bubble']).querySelector('.shopify-section');
    if (bubble && bubbleSection) bubble.innerHTML = bubbleSection.innerHTML;

    const drawer = document.querySelector('cart-drawer');
    const inner = drawer?.querySelector('#CartDrawer');
    const fresh = sections['cart-drawer'] && parseHTML(sections['cart-drawer']).querySelector('#CartDrawer');
    if (inner && fresh) {
      inner.innerHTML = fresh.innerHTML;
      drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', drawer.close.bind(drawer));
      drawer.syncEmptyState?.();
    }
  };

  const cart = {
    data: null,
    listeners: new Set(),
    queue: Promise.resolve(),
    renderedAt: 0,

    items() {
      return this.data?.items || [];
    },

    count() {
      return sumQuantity(this.items().filter((item) => !isProtection(item)));
    },

    quantity(variantId) {
      return sumQuantity(this.items().filter((item) => item.variant_id === variantId));
    },

    line(variantId) {
      return this.items().find((item) => item.variant_id === variantId);
    },

    async load(withSections = false) {
      const requests = [fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json())];
      if (withSections) {
        requests.push(
          fetch(`${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`)
            .then((r) => r.json())
            .catch(() => null),
        );
      }
      const [data, sections] = await Promise.all(requests);
      this.data = data;
      if (sections) renderCartSections(sections);
      this.listeners.forEach((listener) => listener());
    },

    async post(path, body) {
      const response = await fetch(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.description || result.message || '');
      return result;
    },

    // Cart writes run one after the other; each one ends with a fresh cart and re-rendered header / drawer.
    // The returned promise rejects with the error of the write, after the refresh.
    change(task) {
      const job = this.queue.then(task);
      this.queue = job
        .catch(() => {})
        .then(() => this.load(true))
        .catch(() => {});
      return job.then(
        () => this.queue,
        (error) => this.queue.then(() => Promise.reject(error)),
      );
    },

    // Changes made elsewhere (product page, cart drawer, upsell): Dawn re-renders the header cart bubble
    // after each one, so a change of the bubble means the cart changed.
    watch() {
      const bubble = document.getElementById('cart-icon-bubble');
      if (!bubble || this.watching) return;
      this.watching = true;
      let timer;
      new MutationObserver(() => {
        if (Date.now() - this.renderedAt < 800) return;
        clearTimeout(timer);
        timer = setTimeout(() => this.load().catch(() => {}), 200);
      }).observe(bubble, { childList: true, subtree: true, characterData: true });
    },
  };

  // Pack mode: collection links open in the pack view (direct visits are redirected by sections/site-globals.liquid)
  const keepBundleView = (event) => {
    const link = event.target.closest?.('a[href]');
    if (!link || link.target === '_blank') return;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || !COLLECTION_PATH.test(url.pathname) || url.searchParams.has('view')) return;
    url.searchParams.set('view', 'bundle');
    link.href = url.toString();
  };

  class BundleProgress extends HTMLElement {
    connectedCallback() {
      this.tiers = parseTiers(this.dataset.tiers);
      this.max = this.tiers.length ? this.tiers[this.tiers.length - 1].qty : 1;
      this.fill = this.querySelector('.bpr__fill');
      this.message = this.querySelector('.bpr__msg');
      const track = this.querySelector('.bpr__track');
      if (track && !track.querySelector('.bpr__tick')) {
        this.tiers.slice(0, -1).forEach((tier) => {
          const tick = document.createElement('span');
          tick.className = 'bpr__tick';
          tick.style.left = `${(tier.qty / this.max) * 100}%`;
          track.appendChild(tick);
        });
      }
      if (!('static' in this.dataset)) {
        this.onCart = () => this.render();
        cart.listeners.add(this.onCart);
      }
      this.render();
    }

    disconnectedCallback() {
      if (this.onCart) cart.listeners.delete(this.onCart);
    }

    render() {
      const count = cart.data && !('static' in this.dataset) ? cart.count() : parseInt(this.dataset.count, 10) || 0;
      if (this.fill) this.fill.style.width = `${Math.min(count / this.max, 1) * 100}%`;
      if (this.message) this.message.textContent = tierMessage(count, tierState(this.tiers, count));
    }
  }

  class BundlePicker extends HTMLElement {
    connectedCallback() {
      try {
        this.variants = JSON.parse(this.querySelector('[data-bp-variants]').textContent);
      } catch (e) {
        this.variants = [];
      }
      const sizes = this.querySelectorAll('.bp__size');
      this.hasSizes = this.variants.some((variant) => variant.s !== null);
      this.color = this.querySelector('.bp__color.is-selected')?.dataset.value ?? this.variants[0]?.c ?? null;
      this.size = sizes.length === 1 ? sizes[0].dataset.value : null;

      this.addButton = this.querySelector('[data-bp-add]');
      this.stepper = this.querySelector('[data-bp-qty]');
      this.countLabel = this.querySelector('[data-bp-count]');
      this.setupColors();

      this.addEventListener('click', this);
      this.onCart = () => this.render();
      cart.listeners.add(this.onCart);
      // Outside pack mode nothing else loads the cart: read it once so the steppers show what is already in it
      if (!cart.data && !cart.initialLoad) {
        cart.initialLoad = true;
        cart.load().catch(() => {});
      }
      this.render();
    }

    disconnectedCallback() {
      cart.listeners.delete(this.onCart);
    }

    // Colours stay on one row: a fade on the right shows that more colours are hidden (swipe / scroll).
    setupColors() {
      const row = this.querySelector('.bp__colors');
      if (!row) return;
      const update = () => row.classList.toggle('is-more', row.scrollWidth - row.clientWidth - row.scrollLeft > 2);
      row.addEventListener('scroll', update, { passive: true });
      requestAnimationFrame(() => {
        const selected = row.querySelector('.bp__color.is-selected');
        if (selected && selected.offsetLeft + selected.offsetWidth > row.clientWidth) {
          row.scrollLeft = selected.offsetLeft - row.clientWidth / 2;
        }
        update();
      });
    }

    matches(variant, size) {
      return (variant.c === null || variant.c === this.color) && (variant.s === null || variant.s === size);
    }

    variant() {
      if (this.hasSizes && this.size === null) return null;
      return this.variants.find((variant) => this.matches(variant, this.size)) || null;
    }

    handleEvent(event) {
      const color = event.target.closest('.bp__color');
      if (color) {
        this.color = color.dataset.value;
        this.showColor(color);
        this.render();
        return;
      }
      const size = event.target.closest('.bp__size');
      if (size) {
        this.size = size.dataset.value;
        this.render();
        return;
      }
      if (event.target.closest('[data-bp-add]')) {
        this.add();
        return;
      }
      const step = event.target.closest('[data-bp-step]');
      if (step) this.step(parseInt(step.dataset.bpStep, 10));
    }

    // Colour photo on the card + card links to that colour (same as snippets/card-swatches.liquid).
    showColor(button) {
      const card = this.closest('.bb__item');
      if (!card) return;
      const image = card.querySelector('.card__media img');
      if (image && button.dataset.src) {
        image.srcset = button.dataset.srcset;
        image.src = button.dataset.src;
      }
      if (button.dataset.url) {
        card.querySelectorAll('a.full-unstyled-link').forEach((link) => {
          link.href = button.dataset.url;
        });
      }
    }

    render() {
      this.querySelectorAll('.bp__color').forEach((button) => {
        const selected = button.dataset.value === this.color;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      this.querySelectorAll('.bp__size').forEach((button) => {
        const variant = this.variants.find((v) => this.matches(v, button.dataset.value));
        const selected = button.dataset.value === this.size;
        button.classList.toggle('is-unavailable', !variant?.a);
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });

      const variant = this.variant();
      const quantity = variant ? cart.quantity(variant.id) : 0;
      const soldOut = !!variant && !variant.a;
      // A message (choose a size, error) shows in the button itself, so nothing moves under the card.
      const showStepper = quantity > 0 && !this.flashText;
      this.addButton.hidden = showStepper;
      this.stepper.hidden = !showStepper;
      this.countLabel.textContent = quantity;
      this.addButton.disabled = (soldOut && !this.flashText) || !!this.busy;
      this.addButton.textContent = this.flashText || (soldOut ? this.dataset.soldOut : this.dataset.add);
    }

    flash(text) {
      clearTimeout(this.flashTimer);
      this.flashText = text;
      this.classList.add('is-warning');
      this.render();
      this.flashTimer = setTimeout(() => {
        this.flashText = null;
        this.classList.remove('is-warning');
        this.render();
      }, 1800);
    }

    add() {
      const variant = this.variant();
      if (!variant) {
        this.flash(this.dataset.pickSize);
        this.classList.remove('bp--shake');
        void this.offsetWidth;
        this.classList.add('bp--shake');
        return;
      }
      this.write(() => cart.post(`${cartUrl}/add.js`, { items: [{ id: variant.id, quantity: 1 }] }), true);
    }

    step(delta) {
      const variant = this.variant();
      const line = variant && cart.line(variant.id);
      if (!line) return;
      this.write(() => cart.post(`${cartUrl}/change.js`, { id: line.key, quantity: Math.max(0, line.quantity + delta) }));
    }

    async write(task, openDrawer = false) {
      this.busy = true;
      this.classList.add('is-busy');
      try {
        await cart.change(task);
        // Normal mode ("Add to cart"): show the cart, like Dawn's own add to cart
        if (openDrawer && this.dataset.mode === 'cart') {
          document.querySelector('cart-drawer')?.open?.(this.addButton);
        }
      } catch (error) {
        const message = error.message && error.message.length <= 28 ? error.message : this.dataset.error;
        this.busy = false;
        this.flash(message);
      } finally {
        this.busy = false;
        this.classList.remove('is-busy');
        this.render();
      }
    }
  }

  class BundleBar extends HTMLElement {
    connectedCallback() {
      this.progress = this.querySelector('bundle-progress');
      this.tiers = parseTiers(this.progress?.dataset.tiers);
      this.goal = parseInt(new URLSearchParams(window.location.search).get('tier'), 10) || 0;
      this.initialCount = parseInt(this.querySelector('[data-bb-num]').textContent, 10) || 0;

      // Theme preview only: Shopify's preview bar covers the bottom of the screen, sit above it.
      if (document.getElementById('PBarNextFrameWrapper')) this.style.bottom = '6.4rem';

      // Room under the page for the bar
      document.body.classList.add('has-bundle-bar');
      if ('ResizeObserver' in window) {
        new ResizeObserver(() => {
          const offset = (parseFloat(this.style.bottom) || 0) * 10;
          document.body.style.setProperty('--bb-bar-height', `${this.offsetHeight + offset}px`);
        }).observe(this);
      }

      this.addEventListener('click', this);
      document.addEventListener('click', keepBundleView, true);
      cart.listeners.add(() => this.render());
      cart.watch();
      this.render();

      if ('enter' in this.dataset) {
        // First visit of the pack page: pack mode on (the drawer is re-rendered with the pack progress)
        cart.change(() => cart.post(`${cartUrl}/update.js`, { attributes: { _pk_bundle: '1' } })).catch(() => {});
      } else {
        cart.initialLoad = true;
        cart.load().catch(() => {});
      }
    }

    handleEvent(event) {
      if (event.target.closest('[data-bb-checkout][aria-disabled="true"]')) {
        event.preventDefault();
        return;
      }
      if (event.target.closest('[data-bb-clear]')) {
        cart.change(() => cart.post(`${cartUrl}/clear.js`, {})).catch(() => {});
        return;
      }
      if (event.target.closest('[data-bb-exit]')) {
        this.exit();
        return;
      }
      const opener = event.target.closest('[data-bb-open]');
      if (opener) {
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
          drawer.open(opener);
        } else {
          window.location.href = cartUrl;
        }
      }
    }

    // Leave pack mode: back to the normal shop (the cart is kept)
    exit() {
      this.classList.add('is-busy');
      cart
        .change(() => cart.post(`${cartUrl}/update.js`, { attributes: { _pk_bundle: '' } }))
        .catch(() => {})
        .then(() => {
          const url = new URL(window.location.href);
          url.searchParams.delete('view');
          url.searchParams.delete('tier');
          window.location.href = url.toString();
        });
    }

    render() {
      const data = cart.data;
      const count = data ? cart.count() : this.initialCount;
      this.querySelector('[data-bb-num]').textContent = count;
      this.querySelector('[data-bb-unit]').textContent = count === 1 ? 'item' : 'items';

      if (data) {
        const total = this.querySelector('[data-bb-total]');
        const original = this.querySelector('[data-bb-original]');
        const discounted = data.original_total_price > data.total_price;
        total.textContent = formatMoney(data.total_price, data.currency);
        original.hidden = !discounted;
        original.textContent = discounted ? formatMoney(data.original_total_price, data.currency) : '';

        const empty = !data.item_count;
        this.querySelector('[data-bb-clear]').hidden = empty;
        const checkout = this.querySelector('[data-bb-checkout]');
        checkout.classList.toggle('is-disabled', empty);
        checkout.setAttribute('aria-disabled', String(empty));
      }

      // Pack page: highlight the camp reached, or the one chosen in the offers while none is reached yet
      const { reached } = tierState(this.tiers, count);
      const active = reached ? reached.qty : this.goal;
      document.querySelectorAll('.bb__tier').forEach((box) => {
        box.classList.toggle('is-active', parseInt(box.dataset.qty, 10) === active);
      });
    }
  }

  customElements.define('bundle-progress', BundleProgress);
  customElements.define('bundle-picker', BundlePicker);
  customElements.define('bundle-bar', BundleBar);
})();
